(function () {
  const mmPartnersMarqueeInstances = new WeakMap();

  class MMPartnersMarquee {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector("[data-mm-partners-marquee-viewport]");
      this.track = section.querySelector("[data-mm-partners-marquee-track]");
      this.source = section.querySelector("[data-mm-partners-marquee-source]");
      this.speed = Number(section.dataset.mmMarqueeSpeed || 60);
      this.direction = section.dataset.mmMarqueeDirection === "right" ? 1 : -1;

      this.hoverEnabled = section.dataset.mmMarqueeHoverEnabled === "true";
      const hoverFactorRaw = Number(section.dataset.mmMarqueeHoverFactor);
      this.hoverFactor =
        Number.isFinite(hoverFactorRaw) && hoverFactorRaw > 0 ? Math.min(hoverFactorRaw, 1) : 0.3;

      this.baseSpeed = Math.max(this.speed, 1);
      this.currentSpeed = this.baseSpeed;
      this.targetSpeed = this.baseSpeed;
      this.offset = 0;
      this.seqWidth = 0;

      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.rebuildTimer = null;
      this.rafId = null;
      this.lastTime = 0;
      this.lastViewportWidth = null;
      this.hasBuilt = false;
      this.isVisible = true;

      this.reduceMotionMql = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

      this.onResize = () => this.scheduleRebuild();
      this.onPointerEnter = () => {
        this.targetSpeed = this.baseSpeed * this.hoverFactor;
      };
      this.onPointerLeave = () => {
        this.targetSpeed = this.baseSpeed;
      };
      this.onVisibilityChange = () => this.updatePlayState();
      this.onReduceMotionChange = () => this.updatePlayState();
      this.tick = this.tick.bind(this);

      this.init();
    }

    init() {
      if (!this.viewport || !this.track || !this.source) return;

      this.rebuild();
      this.waitForImages(Array.from(this.source.querySelectorAll("img"))).then(() =>
        this.scheduleRebuild(true)
      );
      this.bindResize();
      this.bindHover();
      this.bindVisibility();
    }

    destroy() {
      this.stopLoop();
      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);

      if (this.reduceMotionMql) {
        this.reduceMotionMql.removeEventListener?.("change", this.onReduceMotionChange);
      }

      if (this.hoverEnabled && this.canHover && this.viewport) {
        this.viewport.removeEventListener("mouseenter", this.onPointerEnter);
        this.viewport.removeEventListener("mouseleave", this.onPointerLeave);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      window.clearTimeout(this.rebuildTimer);

      if (this.track) {
        this.track.replaceChildren();
        this.track.style.removeProperty("transform");
        this.track.style.removeProperty("--mm-partners-duration");
        this.track.style.removeProperty("animation");
      }

      this.lastViewportWidth = null;
      this.seqWidth = 0;
      this.hasBuilt = false;
    }

    bindResize() {
      if (typeof ResizeObserver === "function") {
        this.resizeObserver = new ResizeObserver(() => this.scheduleRebuild());
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener("resize", this.onResize);
      }
    }

    bindHover() {
      if (!this.hoverEnabled || !this.canHover) return;
      this.viewport.addEventListener("mouseenter", this.onPointerEnter);
      this.viewport.addEventListener("mouseleave", this.onPointerLeave);
    }

    bindVisibility() {
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      this.reduceMotionMql.addEventListener?.("change", this.onReduceMotionChange);

      if (typeof IntersectionObserver === "function") {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.isVisible = entries[0].isIntersecting;
            this.updatePlayState();
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.section);
      }
    }

    scheduleRebuild(force) {
      if (!force && this.lastViewportWidth !== null && this.viewport) {
        const viewportWidth = this.viewport.getBoundingClientRect().width;

        if (Math.abs(viewportWidth - this.lastViewportWidth) < 8) {
          return;
        }
      }

      window.clearTimeout(this.rebuildTimer);
      this.rebuildTimer = window.setTimeout(() => this.rebuild(), 150);
    }

    rebuild() {
      const sourceItems = Array.from(this.source.querySelectorAll("[data-mm-partners-marquee-source-item]"));
      if (!sourceItems.length) {
        this.track.replaceChildren();
        return;
      }

      // JS drives the scroll — make sure no CSS keyframe animation fights it.
      this.track.style.animation = "none";
      this.track.replaceChildren();

      const sequence = document.createElement("div");
      sequence.className = "mm-partners-marquee__sequence";

      this.appendSourceSet(sequence, sourceItems, false);
      this.track.appendChild(sequence);

      requestAnimationFrame(() => {
        const sourceSetWidth = sequence.getBoundingClientRect().width;
        const viewportWidth = this.viewport.getBoundingClientRect().width;
        const targetWidth = viewportWidth + Math.max(sourceSetWidth, 1);

        let guard = 0;
        while (sequence.getBoundingClientRect().width < targetWidth && guard < 30) {
          this.appendSourceSet(sequence, sourceItems, true);
          guard += 1;
        }

        const duplicateSequence = sequence.cloneNode(true);
        duplicateSequence.setAttribute("aria-hidden", "true");
        this.track.appendChild(duplicateSequence);

        this.lastViewportWidth = viewportWidth;
        this.bindGeneratedImageLoads();
        this.applyAnimation(sequence.getBoundingClientRect().width);
      });
    }

    appendSourceSet(sequence, sourceItems, ariaHidden) {
      sourceItems.forEach((sourceItem) => {
        const item = sourceItem.cloneNode(true);
        this.sanitizeGeneratedNode(item);

        if (ariaHidden) {
          item.setAttribute("aria-hidden", "true");
        }

        sequence.appendChild(item);
      });
    }

    sanitizeGeneratedNode(node) {
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      Array.from(node.attributes).forEach((attribute) => {
        if (attribute.name.indexOf("data-shopify") === 0 || attribute.name === "block-id") {
          node.removeAttribute(attribute.name);
        }
      });

      if (node.tagName === "IMG") {
        node.loading = "eager";
        node.decoding = "async";
      }

      node.querySelectorAll("*").forEach((child) => this.sanitizeGeneratedNode(child));
    }

    bindGeneratedImageLoads() {
      if (this.hasBuilt) return;

      this.track.querySelectorAll("img").forEach((image) => {
        if (image.complete) return;

        image.addEventListener("load", this.onResize, { once: true });
        image.addEventListener("error", this.onResize, { once: true });
      });
    }

    applyAnimation(sequenceWidth) {
      this.seqWidth = Math.max(sequenceWidth, 1);
      this.track.style.animation = "none";

      // Keep our scroll offset within the (possibly new) sequence width.
      this.offset = this.offset % this.seqWidth;
      this.hasBuilt = true;

      this.updatePlayState();
    }

    updatePlayState() {
      const reduceMotion = this.reduceMotionMql.matches;
      const active =
        !reduceMotion && this.isVisible && document.visibilityState !== "hidden" && this.seqWidth > 0;

      if (active) {
        this.startLoop();
      } else {
        this.stopLoop();
      }
    }

    startLoop() {
      if (this.rafId !== null) return;
      this.lastTime = 0;
      this.rafId = requestAnimationFrame(this.tick);
    }

    stopLoop() {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    tick(now) {
      this.rafId = requestAnimationFrame(this.tick);

      if (!this.lastTime) {
        this.lastTime = now;
        return;
      }

      let dt = (now - this.lastTime) / 1000;
      this.lastTime = now;

      // Clamp delta so a backgrounded/janky frame can't teleport the track.
      if (dt > 0.05) dt = 0.05;

      // Ease current speed toward target (smooth hover slow-down / speed-up).
      const smoothing = 1 - Math.exp(-dt / 0.18);
      this.currentSpeed += (this.targetSpeed - this.currentSpeed) * smoothing;

      this.offset += this.currentSpeed * dt;

      if (this.seqWidth > 0) {
        this.offset = this.offset % this.seqWidth;
        const x = this.direction === -1 ? -this.offset : this.offset - this.seqWidth;
        this.track.style.transform = `translate3d(${x}px, 0, 0)`;
      }
    }

    waitForImages(images) {
      const pendingImages = images.filter((image) => !image.complete);

      if (!pendingImages.length) {
        return Promise.resolve();
      }

      return Promise.all(
        pendingImages.map(
          (image) =>
            new Promise((resolve) => {
              image.addEventListener("load", resolve, { once: true });
              image.addEventListener("error", resolve, { once: true });
            })
        )
      );
    }
  }

  function initMMPartnersMarquee(container = document) {
    container.querySelectorAll("[data-mm-partners-marquee-section]").forEach((section) => {
      if (mmPartnersMarqueeInstances.has(section)) return;
      mmPartnersMarqueeInstances.set(section, new MMPartnersMarquee(section));
    });
  }

  function unloadMMPartnersMarquee(section) {
    const instance = mmPartnersMarqueeInstances.get(section);
    if (!instance) return;

    instance.destroy();
    mmPartnersMarqueeInstances.delete(section);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initMMPartnersMarquee());
  } else {
    initMMPartnersMarquee();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initMMPartnersMarquee(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-mm-partners-marquee-section]").forEach((section) => {
      unloadMMPartnersMarquee(section);
    });
  });
})();